import React, { useState, useEffect } from 'react';
import DailyReportTitleContainer from './DailyReportListTitleContainer';
import DailyReportTableContainer from './DailyReportListTableContainer';
import { useNavigate,useLocation } from 'react-router-dom';
import api from '../api/api';
import DailyReportFilter from './DailyReportFilter';
import DailyReportSettings from './DailyReportSettings';

const DailyReportListMain = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [currentPage, setCurrentPage] = useState(1); // ページ番号を管理
    const [hasMore, setHasMore] = useState(true); // "more" ボタンの表示制御
    const [loading, setLoading] = useState(false); // データロード中かどうかを管理
    const [sortBy, setSortBy] = useState('date'); // ソート条件
    const [companyList, setCompanyList] = useState([]);
    const [originalReports, setOriginalReports] = useState([]);
    const [filterByDate, setFilterByDate] = useState(false);
    const [filterByCompany, setFilterByCompany] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedSecCode, setSelectedSecCode] = useState(null);
    const [filteredData, setFilteredData] = useState([]);
    const [showDailyReportSettings, setShowDailyReportSettings] = useState(false);

    const groupByDate = (reportList) => {
        return reportList.reduce((acc, report) => {
            const existingDateGroup = acc.find(group => group.date === report.date);
            if (existingDateGroup) {
                existingDateGroup.dailyReports.push({
                    secCode: report.sec_code,
                    companyName: report.company_name,
                    contents: report.contents,
                });
            } else {
                acc.push({
                    date: report.date,
                    dailyReports: [{
                        secCode: report.sec_code,
                        companyName: report.company_name,
                        contents: report.contents,
                    }],
                });
            }
            return acc;
        }, []);
    };

    const filterData = (data) => {
        let filteredReports = [];
        if (selectedDate && selectedSecCode) {
            filteredReports = data.filter(report => report.date === selectedDate && report.sec_code === selectedSecCode);
        } else if (filterByDate) {
            filteredReports = data.filter(report => report.date === selectedDate);
        } else if (filterByCompany) {
            filteredReports = data.filter(report => report.sec_code === selectedSecCode);
        } else {
            filteredReports = data;
        }
        return groupByDate(filteredReports);
    }

    const fetchDailyReportList = async (page, resetList = false, secCode = null, date = null) => {
        setLoading(true);
        try {
            let query = `${process.env.REACT_APP_API_URL}/chat/api/daily-report-list/?page=${page}&sort_by=${sortBy}`;

            if (secCode) {
                query += `&sec_code=${secCode}`;
            }

            if (date) {
                query += `&date=${date}`;
            }

            const response = await api.get(query);
            const data = filterData(response.data.results);
            setOriginalReports(prev => resetList ? response.data.results : [...prev, ...response.data.results]);
            setHasMore(response.data.next !== null);
            setFilteredData(prev => resetList ? data : [...prev, ...data]);  // 追加データかリセットかを管理
        } catch (error) {
            if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                navigate('/login', { state: { from: location.pathname } });
            } else {
                console.error('Error fetching daily report list:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchDailyReportCompanyList = async () => {
        try {
            const response = await api.get(`${process.env.REACT_APP_API_URL}/chat/api/daily-report-company-list/`);
            const data = response.data;

            // sec_codeで昇順に並び替え
            data.sort((a, b) => (a.sec_code < b.sec_code ? -1 : 1));
            setCompanyList(data);
        } catch (error) {
            if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                navigate('/login');
                navigate('/login', { state: { from: location.pathname } });
            } else {
                console.error('Error fetching company list:', error);
            }
        }
    };

    // 初回ロード時、会社リストをフェッチ
    useEffect(() => {
        fetchDailyReportCompanyList();
    }, []);

    // ページ番号の変化に応じてデータをフェッチ
    useEffect(() => {
        fetchDailyReportList(currentPage, currentPage === 1); // ページが変更されたらリセット
    }, [currentPage, sortBy]);  // ソート条件が変更されたらリセット

    // 日付やsecCodeが変更されたときにデータを再フィルタリング
    useEffect(() => {
        if (originalReports.length > 0) {
            const filteredReports = filterData(originalReports);
            setFilteredData(filteredReports);
        }
    }, [selectedDate, selectedSecCode, originalReports]);

    const handleShowMore = () => {
        if (hasMore && !loading) {
            setCurrentPage(prevPage => prevPage + 1); // 次のページを設定
        }
    };

    const handleDateChange = (date) => {
        setSelectedDate(date ? date : null);
        setFilterByDate(!!date);
        setCurrentPage(1);  // ページをリセットしてデータを再取得
        fetchDailyReportList(1, true, selectedSecCode, date);
    }

    const handleCompanyChange = (secCode) => {
        if (secCode === "all") {
            setFilterByCompany(false);
            setSelectedSecCode(null);
        } else {
            setFilterByCompany(true);
            setSelectedSecCode(secCode);
        }
        setCurrentPage(1);  // ページをリセットしてデータを再取得
        if (secCode === "all") {
            fetchDailyReportList(1, true, null, selectedDate);
        } else {
            fetchDailyReportList(1, true, secCode, selectedDate);
        }
        
    }

    const handleSettingsCloseClick = () => {
        setShowDailyReportSettings(false);
    }

    return (
        <div className='main-contents'>
            <DailyReportTitleContainer showDailyReportSettings={showDailyReportSettings} setShowDailyReportSettings={setShowDailyReportSettings} />
            {showDailyReportSettings && <DailyReportSettings handleCloseClick={handleSettingsCloseClick} />}
            <DailyReportFilter
                companies={companyList}
                handleDateChange={handleDateChange}
                handleCompanyChange={handleCompanyChange}
            />
            <DailyReportTableContainer
                dailyReportList={filteredData}
                hasMore={hasMore}
                loading={loading}
                handleShowMore={handleShowMore}
                sortBy={sortBy}
                setSortBy={setSortBy}
            />
        </div>
    );
};

export default DailyReportListMain;
